import React, { useMemo } from 'react'
import { isEqual } from 'lodash'

import {
  Completion,
  CompletionRole,
  CompletionState,
  FunctionCallState,
  PilotContextType,
} from '../../types'
import Bubble from './Bubble'
import PaymentWidget from '../../components/PaymentWidget'
import { useAppSelector } from '../../hooks'
import { getCompletionById } from './selectors'
import PilotContext from 'bundles/Pilot/contexts/PilotContext'
import { getCompletionMessage } from '../../utils/functionCallMessages'

type Props = {
  readonly completionId: number
}

interface PilotFunctionCall {
  id: number
  functionName: string
  state: FunctionCallState
  errorMessage?: string
  result?: {
    message?: string
    iframe_url?: string
    iframeUrl?: string
  }
}

function parseAuthError(errorMessage?: string): {
  isAuthError: boolean
  message?: string
  signInUrl?: string
  signUpUrl?: string
} {
  if (!errorMessage) return { isAuthError: false }

  if (
    errorMessage.includes('authentication required') ||
    errorMessage.includes('not authenticated') ||
    errorMessage.includes('sign in required')
  ) {
    return {
      isAuthError: true,
      message: errorMessage,
      signInUrl: '/users/sign_in',
      signUpUrl: '/users/sign_up',
    }
  }

  return { isAuthError: false }
}

function ListItem({ completionId }: Props) {
  const completion = useAppSelector(
    state => getCompletionById(state, completionId),
    isEqual
  ) as Completion
  const functionCallProgress = useAppSelector(state => state.completions.functionCallProgress)
  const { authenticated } = React.useContext<PilotContextType>(PilotContext)

  const activeFunctionCall = completion.pilotFunctionCalls?.find(
    fc => fc.state === FunctionCallState.Pending || fc.state === FunctionCallState.Executing
  )
  const progressMessage = activeFunctionCall && functionCallProgress[activeFunctionCall.id]
  const shouldShowThinkingBubble =
    completion.state === CompletionState.Processing &&
    !completion.assistantResponse &&
    !activeFunctionCall

  const isDisplayPaymentWidgetCall = (functionCall: PilotFunctionCall) => {
    return functionCall.functionName === 'display_payment_widget'
  }

  const isCompletedNonPaymentCall = (functionCall: PilotFunctionCall) => {
    return (
      !isDisplayPaymentWidgetCall(functionCall) &&
      functionCall.state === FunctionCallState.Completed
    )
  }

  const isFailedAuthenticatedCall = (functionCall: PilotFunctionCall) => {
    if (functionCall.state !== FunctionCallState.Failed) return false
    const authInfo = parseAuthError(functionCall.errorMessage)
    return !authInfo.isAuthError || authenticated
  }

  const isCompletedPaymentWidgetCall = (functionCall: PilotFunctionCall) => {
    return (
      isDisplayPaymentWidgetCall(functionCall) && functionCall.state === FunctionCallState.Completed
    )
  }

  const hasCompletedPaymentWidget = completion.pilotFunctionCalls?.some(
    isCompletedPaymentWidgetCall
  )

  const renderUserRequestBubble = () => (
    <Bubble
      completionId={completionId}
      content={completion.userRequest}
      rating={completion.rating}
      role={CompletionRole.User}
      state={CompletionState.Completed}
      updating={completion.updating}
      files={completion.pilotFiles}
    />
  )

  const renderCompletedFunctionCalls = () => {
    return completion.pilotFunctionCalls
      ?.filter(isCompletedNonPaymentCall)
      .map(functionCall => (
        <Bubble
          key={`function-${functionCall.id}`}
          completionId={completionId}
          content={functionCall.result?.message ?? getCompletionMessage(functionCall.functionName)}
          rating={0}
          role={CompletionRole.Assistant}
          state={CompletionState.Completed}
          functionCall={functionCall}
        />
      ))
  }

  const renderDevelopmentErrorBubbles = () => {
    if (process.env.NODE_ENV !== 'development') return null

    return completion.pilotFunctionCalls
      ?.filter(isFailedAuthenticatedCall)
      .map(functionCall => (
        <Bubble
          key={`function-error-${functionCall.id}`}
          completionId={completionId}
          content={
            functionCall.errorMessage ||
            'Sorry, an error occurred. Please try again in a few minutes.'
          }
          rating={0}
          role={CompletionRole.Assistant}
          state={CompletionState.Failed}
        />
      ))
  }

  const renderAssistantResponse = () => {
    if (!completion.assistantResponse) return null

    if (hasCompletedPaymentWidget) return null

    return (
      <Bubble
        completionId={completionId}
        content={completion.assistantResponse}
        rating={completion.rating}
        role={CompletionRole.Assistant}
        state={completion.state}
        updating={completion.updating}
      />
    )
  }

  const renderPaymentWidgets = useMemo(() => {
    return completion.pilotFunctionCalls?.filter(isCompletedPaymentWidgetCall).map(functionCall => {
      const iframeUrl = functionCall.result?.iframe_url || functionCall.result?.iframeUrl

      if (!iframeUrl) return null

      return (
        <div key={`payment-widget-${functionCall.id}`} className='payment-widget-container'>
          <PaymentWidget
            src={iframeUrl}
            height={400}
            title='Payment Widget'
            className='pilot-iframe-bubble'
            assistantMessage={completion.assistantResponse}
          />
        </div>
      )
    })
  }, [completion.pilotFunctionCalls, completion.assistantResponse])

  const renderThinkingBubble = () => {
    if (!shouldShowThinkingBubble && !activeFunctionCall) return null

    return (
      <Bubble
        completionId={completionId}
        content={progressMessage || 'Thinking...'}
        rating={0}
        role={CompletionRole.Assistant}
        state={CompletionState.Processing}
        functionCall={activeFunctionCall}
      />
    )
  }

  return (
    <>
      {renderUserRequestBubble()}
      {renderCompletedFunctionCalls()}
      {renderDevelopmentErrorBubbles()}
      {renderAssistantResponse()}
      {renderPaymentWidgets}
      {renderThinkingBubble()}
    </>
  )
}

export default ListItem
